import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, AlertCircle, CheckCircle, X, 
  TrendingDown, TrendingUp, Clock, Package, DollarSign
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

export function FinancialApprovalPage() {
  const navigate = useNavigate();
  const { soId } = useParams<{ soId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState<'purchase' | 'logistics'>('purchase');

  const [deal, setDeal] = useState<any>(null);
  const [po, setPo] = useState<any>(null);
  const [logistics, setLogistics] = useState<any>(null);
  const [form, setForm] = useState({
    approved: false,
    rejectionReason: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [soId, type]);

  const fetchData = async () => {
    if (!soId) return;
    setLoading(true);
    try {
      const soRes = await api.get(`/sales-orders/${soId}`);
      const so = soRes.data.data;
      setDeal(so);

      if (type === 'purchase') {
        // Get linked POs via SO-PO mapping
        const mappingRes = await api.get(`/so-po-mapping/sales-orders/${soId}`);
        const mappings = mappingRes.data.data || [];
        const firstPoId = mappings.find((m: any) => m.purchaseOrderId)?.purchaseOrderId;
        if (firstPoId) {
          const poRes = await api.get(`/purchase-orders/${firstPoId}`);
          setPo(poRes.data.data);
        }
      } else {
        const logRes = await api.get(`/logistics/quotes?soId=${soId}`);
        setLogistics(logRes.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateMargin = () => {
    if (!deal) return { margin: 0, marginPct: 0 };
    const sellTotal = Number(deal.total) || 0;
    const commodityCost = Number(deal.config?.commodityCost) || (po ? Number(po.total) : 0);
    const logisticsCost = Number(deal.config?.logisticsCost) || (logistics?.[0]?.rate || 0) * (deal.items?.[0]?.quantity || 0);
    const margin = sellTotal - commodityCost - logisticsCost;
    const marginPct = sellTotal > 0 ? (margin / sellTotal) * 100 : 0;
    return { margin, marginPct };
  };

  const handleSubmit = async () => {
    if (!form.approved && !form.rejectionReason) {
      alert('Please provide rejection reason');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/sales-orders/${soId}/financial-approval`, {
        type,
        approved: form.approved,
        rejectionReason: form.rejectionReason,
        notes: form.notes,
      });
      navigate(`/sales-orders/${soId}`);
    } catch (err) {
      alert('Failed to submit approval');
    } finally {
      setSaving(false);
    }
  };

  const { margin, marginPct } = calculateMargin();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Approval</h1>
          <p className="text-sm text-gray-500">Deal: {deal?.number}</p>
        </div>
        <div className="flex rounded-lg border border-gray-200 bg-white p-1">
          <button onClick={() => setType('purchase')} className={`px-3 py-1.5 rounded-md text-sm ${type === 'purchase' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'text-gray-600'}`}>Purchase</button>
          <button onClick={() => setType('logistics')} className={`px-3 py-1.5 rounded-md text-sm ${type === 'logistics' ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'text-gray-600'}`}>Logistics</button>
        </div>
      </div>

      {/* Deal Summary */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Deal Summary</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500">Commodity</p>
            <p className="font-medium">{deal?.config?.commodity || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Quantity (MT)</p>
            <p className="font-medium">{deal?.items?.[0]?.quantity || 0}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Sell Rate</p>
            <p className="font-medium">{formatINR(deal?.items?.[0]?.rate || 0)}/MT</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Value</p>
            <p className="font-medium">{formatINR(deal?.total || 0)}</p>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Cost Breakdown</h2>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Sell Price</span>
            <span className="font-medium">{formatINR(deal?.total || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">{type === 'purchase' ? 'Commodity Cost' : 'Logistics Cost'}</span>
            <span className="font-medium">{formatINR(type === 'purchase' ? (po?.total || 0) : ((logistics?.[0]?.rate || 0) * (deal?.items?.[0]?.quantity || 0)))}</span>
          </div>
          <div className="border-t pt-3 flex justify-between">
            <span className="font-medium text-gray-900">Deal Margin</span>
            <span className={`font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatINR(margin)} ({marginPct.toFixed(1)}%)
            </span>
          </div>
        </div>
      </div>

      {/* Approval Form */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Approval Decision</h2>
        <div className="flex gap-4 mb-4">
          <label className={`flex-1 cursor-pointer rounded-lg border-2 p-4 transition-colors ${form.approved ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input type="radio" checked={form.approved} onChange={() => setForm({ ...form, approved: true })} className="sr-only" />
            <div className="flex items-center gap-2">
              <CheckCircle className={form.approved ? 'text-green-600' : 'text-gray-400'} />
              <span className={`font-medium ${form.approved ? 'text-green-700' : 'text-gray-600'}`}>Approve</span>
            </div>
          </label>
          <label className={`flex-1 cursor-pointer rounded-lg border-2 p-4 transition-colors ${!form.approved && form.rejectionReason ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input type="radio" checked={!form.approved} onChange={() => setForm({ ...form, approved: false })} className="sr-only" />
            <div className="flex items-center gap-2">
              <X className={!form.approved && form.rejectionReason ? 'text-red-600' : 'text-gray-400'} />
              <span className={`font-medium ${!form.approved && form.rejectionReason ? 'text-red-700' : 'text-gray-600'}`}>Reject</span>
            </div>
          </label>
        </div>
        
        {!form.approved && (
          <div className="mb-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Rejection Reason</label>
            <textarea value={form.rejectionReason} onChange={e => setForm({ ...form, rejectionReason: e.target.value })} rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Explain why deal is rejected..." />
          </div>
        )}
        
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Additional notes..." />
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</button>
        <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[var(--ink-0)] text-white rounded-lg text-sm hover:bg-[var(--ink-1)] disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Submit Approval
        </button>
      </div>
    </div>
  );
}